import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Input } from "../components/forms/Input";
import { Select } from "../components/forms/Select";
import { Button } from "../components/Button";
import { US_STATES } from "../types";
import { usePageTitle } from "../hooks/usePageTitle";

export function Checkout() {
  usePageTitle("Checkout");
  const { items, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    shipping_address: "",
    shipping_city: "",
    shipping_state: "",
    shipping_zip: "",
  });

  const tax = total * 0.08;
  const shipping = 10.0;
  const finalTotal = total + tax + shipping;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create order
      const orderData = {
        ...formData,
        items: items.map((item) => ({
          product_id: item.product.id,
          product_name: item.product.name,
          product_price: item.product.price,
          quantity: item.quantity,
        })),
      };

      const response = await fetch("/api/orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) throw new Error("Failed to create order");

      const order = await response.json();

      // Clear cart and navigate to confirmation
      clearCart();
      navigate(`/order/${order.order_number}`);
    } catch (error) {
      console.error("Error creating order:", error);
      alert("Failed to create order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    navigate("/cart");
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-8">Checkout</h1>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold mb-4">Customer Information</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Full Name"
              name="customer_name"
              value={formData.customer_name}
              onChange={handleChange}
              required
            />
            <Input
              label="Email"
              name="customer_email"
              type="email"
              value={formData.customer_email}
              onChange={handleChange}
              required
            />
            <Input
              label="Phone"
              name="customer_phone"
              type="tel"
              value={formData.customer_phone}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold mb-4">Shipping Address</h2>
          <div className="space-y-4">
            <Input
              label="Address"
              name="shipping_address"
              value={formData.shipping_address}
              onChange={handleChange}
              required
            />
            <div className="grid md:grid-cols-3 gap-4">
              <Input
                label="City"
                name="shipping_city"
                value={formData.shipping_city}
                onChange={handleChange}
                required
              />
              <Select
                label="State"
                name="shipping_state"
                value={formData.shipping_state}
                onChange={handleChange}
                required
              >
                <option value="">Select state</option>
                {US_STATES.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </Select>
              <Input
                label="ZIP Code"
                name="shipping_zip"
                value={formData.shipping_zip}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold mb-4">Order Summary</h2>
          <div className="space-y-2">
            {items.map((item) => (
              <div key={item.product.id} className="flex justify-between text-sm">
                <span>
                  {item.product.name} × {item.quantity}
                </span>
                <span>${(item.product.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            <div className="border-t pt-2 space-y-1">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>${total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Tax</span>
                <span>${tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Shipping</span>
                <span>${shipping.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg">
                <span>Total</span>
                <span>${finalTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        <Button
          type="submit"
          variant="primary"
          className="w-full text-lg"
          loading={loading}
        >
          Place Order
        </Button>
      </form>
    </div>
  );
}
